import React from 'react';
import {
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { FeatureFlagsTableProps } from '../../../types/features/featureFlags';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';

const FeatureFlagsTable: React.FC<FeatureFlagsTableProps> = ({
  error,
  loading,
  onRowClick,
  onRequestSort,
  order,
  orderBy,
  rows,
}) => (
  <TableContainer>
    <Table size="small" sx={{ minWidth: 980, tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow sx={{ background: '#F3F2F7', color: '#222B45' }}>
          <TableCell sx={{ color: '#222B45', fontSize: '12px', fontWeight: 600, lineHeight: 1.15, p: '6px 8px', width: '32%' }}>
            Feature
          </TableCell>
          <TableCell sx={{ color: '#222B45', fontSize: '12px', fontWeight: 600, lineHeight: 1.15, p: '6px 8px', width: '16%' }}>
            Target Client
          </TableCell>
          <TableCell sx={{ color: '#222B45', fontSize: '12px', fontWeight: 600, lineHeight: 1.15, p: '6px 8px', width: '12%' }}>
            Global
          </TableCell>
          <TableCell sx={{ color: '#222B45', fontSize: '12px', fontWeight: 600, lineHeight: 1.15, p: '6px 8px', width: '16%' }}>
            Rollout
          </TableCell>
          <TableCell align="right" sx={{ color: '#222B45', fontSize: '12px', fontWeight: 600, lineHeight: 1.15, p: '6px 18px 6px 8px', width: 88 }}>
            <TableSortLabel
              active={orderBy === 'assignmentCount'}
              direction={order}
              hideSortIcon={false}
              onClick={() => onRequestSort('assignmentCount')}
              sx={{
                '& .MuiTableSortLabel-icon': {
                  color: orderBy === 'assignmentCount' ? '#222B45 !important' : '#9AA3AF !important',
                  opacity: 1,
                },
                color: '#222B45 !important',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <span>Count</span>
            </TableSortLabel>
          </TableCell>
          <TableCell sx={{ color: '#222B45', fontSize: '12px', fontWeight: 600, lineHeight: 1.15, p: '6px 8px 6px 32px', width: 234 }}>
            Last Updated
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ borderBottom: 0, py: 7, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </TableCell>
          </TableRow>
        ) : error ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ borderBottom: 0, py: 7, textAlign: 'center' }}>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.danger }}>
                {error}
              </Typography>
            </TableCell>
          </TableRow>
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ borderBottom: 0, py: 7, textAlign: 'center' }}>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                No feature flags found
              </Typography>
            </TableCell>
          </TableRow>
        ) : rows.map((row, index) => (
          <TableRow
            key={row.id}
            hover
            onClick={() => onRowClick(row)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onRowClick(row);
              }
            }}
            role="button"
            tabIndex={0}
            sx={{
              background: index % 2 === 0 ? '#FFFFFF' : '#FAF8FFCC',
              cursor: 'pointer',
              height: 34,
            }}
          >
            <TableCell sx={{ borderBottomColor: '#ECEEF3', lineHeight: 1.2, p: '4px 8px' }}>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                {row.displayName}
              </Typography>
              <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                {row.featureKey}
              </Typography>
            </TableCell>
            <TableCell sx={{ borderBottomColor: '#ECEEF3', lineHeight: 1.2, p: '4px 8px' }}>{row.clientLabel}</TableCell>
            <TableCell sx={{ borderBottomColor: '#ECEEF3', lineHeight: 1.2, p: '4px 8px' }}>
              <Switch
                size="small"
                checked={row.globalEnabled}
                inputProps={{
                  'aria-label': `${row.displayName} global state`,
                  readOnly: true,
                  tabIndex: -1,
                }}
                sx={{ pointerEvents: 'none' }}
              />
            </TableCell>
            <TableCell sx={{ borderBottomColor: '#ECEEF3', lineHeight: 1.2, p: '4px 8px' }}>{row.rolloutLabel}</TableCell>
            <TableCell align="right" sx={{ borderBottomColor: '#ECEEF3', lineHeight: 1.2, p: '4px 18px 4px 8px', width: 88 }}>{row.assignmentCountLabel}</TableCell>
            <TableCell sx={{ borderBottomColor: '#ECEEF3', lineHeight: 1.2, p: '4px 8px 4px 32px' }}>{row.lastUpdated}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default React.memo(FeatureFlagsTable);
